import * as yup from "yup";
import { Form } from "@/components/organisms/Form";
import { request } from "@/common/api";
import { Buttons } from "@/components/atoms/Buttons";
import { showAlert } from "@/components/containers/Alert";
import { useNavigate } from "react-router";
import { yupResolver } from "@hookform/resolvers/yup";
import { TextControl } from "@/components/containers/Controls/TextControl";
import { BaseResponse } from "@/common/types/baseRespones";
import { FormInputBox } from "@/components/molecules/FormInputBox";
import { MainTemplate } from "@/components/templates/MainTemplate";
import { GET_USER_INFO } from "@/common/querykeys";
import { useRecoilValue } from "recoil";
import { loginTypeState } from "@/common/states/user";
import { Guardian, Student } from "@/common/types/student";
import { Controller, useForm } from "react-hook-form";
import { useMutation, useQuery } from "@tanstack/react-query";
import { ReactComponent as IconArrow } from "@/styles/assets/svg/icon_arrow.svg";

const schema = yup.object({
  user: yup.object({
    password: yup
      .string()
      .required("비밀번호는 필수 입력입니다.")
      .min(8, "비밀번호는 최소 8자 이상이어야 합니다.")
      .max(20, "비밀번호는 최대 20자 이하이어야 합니다."),
    newPassword: yup
      .string()
      .required("비밀번호는 필수 입력입니다.")
      .test(
        "not-same-as-old",
        "새 비밀번호는 기존 비밀번호와 같을 수 없습니다.",
        function (value) {
          const { password } = this.parent;
          return password !== value;
        }
      )
      .min(8, "비밀번호는 최소 8자 이상이어야 합니다.")
      .max(20, "비밀번호는 최대 20자 이하이어야 합니다."),
    newPasswordCheck: yup
      .string()
      .oneOf([yup.ref("newPassword")], "비밀번호가 일치하지 않습니다.")
      .required("비밀번호 재확인은 필수 입력입니다."),
  }),
});

export const UserPwEditPage = () => {
  const navigate = useNavigate();
  const loginType = useRecoilValue(loginTypeState);
  const { control, handleSubmit } = useForm({
    resolver: yupResolver(schema),
  });

  const { data: student } = useQuery<Student>({
    queryKey: [GET_USER_INFO],
    queryFn: async () => {
      return await request<Student>({
        method: "GET",
        url: "/student/info",
      });
    },
    enabled: loginType === "student",
  });

  const { data: guardian } = useQuery<Guardian>({
    queryKey: [GET_USER_INFO],
    queryFn: async () => {
      return await request<Guardian>({
        method: "GET",
        url: "/guardian/info",
      });
    },
    enabled: loginType === "guardian",
  });

  const { mutateAsync: updateStudentPw } = useMutation({
    mutationFn: (data: {
      id: string;
      userId: string;
      password: string;
      newPassword: string;
    }) => {
      return request<BaseResponse>({
        method: "PUT",
        url: "student/edit-pw",
        data: data,
      });
    },
  });

  const { mutateAsync: updateGuardianPw } = useMutation({
    mutationFn: (data: {
      id: string;
      userId: string;
      password: string;
      newPassword: string;
    }) => {
      return request<BaseResponse>({
        method: "PUT",
        url: "guardian/edit-pw",
        data: data,
      });
    },
  });

  const onSubmit = async (data: any) => {
    try {
      if (loginType === "student") {
        if (student) {
          await updateStudentPw({
            id: student.id,
            userId: student.userId,
            password: data.user.password,
            newPassword: data.user.newPassword,
          });
          navigate("/setting/user-editType");
          showAlert("변경 되었습니다.");
        }
      } else {
        if (guardian) {
          await updateGuardianPw({
            id: guardian.id,
            userId: guardian.userId,
            password: data.user.password,
            newPassword: data.user.newPassword,
          });
          navigate("/setting/user-editType");
          showAlert("변경 되었습니다.");
        }
      }
    } catch (error) {
      showAlert(
        "현재 비밀번호를 잘못 입력했습니다.\n입력하신 내용을 다시 확인해주세요."
      );
    }
  };

  return (
    <MainTemplate
      nav={false}
      subPage={true}
      menuBtn={false}
      buttonLeft={
        <Buttons
          type="button"
          size="headerTextIcon"
          layout="text"
          color="bright100"
          icon={<IconArrow />}
          label="뒤로"
          onClick={() => window.history.back()}
        />
      }
    >
      <Form
        title="비밀번호 변경"
        submitBtn={
          <Buttons
            type="submit"
            size="lg"
            color="primary100"
            layout="solid"
            label="변경"
            onClick={handleSubmit(onSubmit)}
          />
        }
        backBtn={
          <Buttons
            type="button"
            size="lg"
            color="primary100"
            layout="text"
            label="취소"
            onClick={() => window.history.back()}
          />
        }
      >
        <FormInputBox>
          <>
            <Controller
              name="user.password"
              control={control}
              defaultValue=""
              render={({ field }) => (
                <TextControl
                  {...field}
                  control={control}
                  name="user.password"
                  label="현재 비밀번호"
                  placeholder="아이디를 입력하세요."
                  required={true}
                />
              )}
            />
            <Controller
              name="user.newPassword"
              control={control}
              defaultValue=""
              render={({ field }) => (
                <TextControl
                  {...field}
                  control={control}
                  name="user.newPassword"
                  label="새 비밀번호"
                  placeholder="새 비밀번호를 입력하세요."
                  required={true}
                  instructions="pw"
                />
              )}
            />
            <Controller
              name="user.newPasswordCheck"
              control={control}
              defaultValue=""
              render={({ field }) => (
                <TextControl
                  {...field}
                  control={control}
                  name="user.newPasswordCheck"
                  label="새 비밀번호 확인"
                  placeholder="새 비밀번호를 다시 입력하세요."
                  required={true}
                />
              )}
            />
          </>
        </FormInputBox>
      </Form>
    </MainTemplate>
  );
};
